import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  Button,
  Breadcrumb,
  Alert,
  Table,
  Spin,
  Flex,
  Form,
  Tabs,
  Empty,
  Space,
} from "antd";
import { ArrowLeftOutlined, HomeOutlined } from "@ant-design/icons";
import { FormProvider, useForm } from "react-hook-form";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
} from "../../components/Component";
import { workOrderReports, reportConfigs } from "../../data/reportsData";
import ReportsApis from "../../api/WorkOrderManagement/Reports.Apis";
import dayjs from "dayjs";
import Export from "../../components/export/Export";
import SubReportDrawer from '../../components/reports/SubReportDrawer';
import { getSubReportConfig } from '../../data/subReportsData';

interface ReportViewProps {
  reportId?: string;
  onBack?: () => void;
  onDrillDown?: (reportId: string, filters?: any) => void;
}

const ReportView: React.FC<ReportViewProps> = ({
  reportId: propReportId,
  onBack,
  onDrillDown,
}) => {
  const { reportId: paramReportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const flowType = searchParams.get("flowType");
  const methods = useForm({ mode: "onChange" });

  const currentReportId = propReportId || paramReportId;
  const reportConfig = currentReportId ? reportConfigs[currentReportId] : null;
  const reportInfo = currentReportId
    ? workOrderReports.find((r) => r.id === currentReportId)
    : null;

  const [dataSource, setDataSource] = useState<any>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    defaultCurrent: 1,
    defaultPageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "40", "50"],
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [activeTab, setActiveTab] = useState<string>(
    reportConfig?.tabs?.[0]?.key || ""
  );

  // Sub-report state
  const [subReportVisible, setSubReportVisible] = useState(false);
  const [subReportData, setSubReportData] = useState<any[]>([]);
  const [subReportLoading, setSubReportLoading] = useState(false);
  const [subReportTitle, setSubReportTitle] = useState('');
  const [subReportColumns, setSubReportColumns] = useState<any[]>([]);
  const [activeSubReportId, setActiveSubReportId] = useState<string>('');
  const [drillDownRecord, setDrillDownRecord] = useState<any>(null);

  // nested sub-report state
  const [nestedSubReportVisible, setNestedSubReportVisible] = useState(false);
  const [nestedSubReportData, setNestedSubReportData] = useState<any[]>([]);
  const [nestedSubReportLoading, setNestedSubReportLoading] = useState(false);
  const [nestedSubReportTitle, setNestedSubReportTitle] = useState('');
  const [nestedSubReportColumns, setNestedSubReportColumns] = useState<any[]>([]);
  const [activeNestedSubReportId, setActiveNestedSubReportId] = useState<string>('');
  const [nestedDrillDownRecord, setNestedDrillDownRecord] = useState<any>(null);

  const getSubReportApiFunction = (subReportId: string) => {
    const subReportConfig = getSubReportConfig(subReportId);
    return subReportConfig?.apiFunction || subReportId;
  };

  // Map report IDs to API function names
  const getApiFunction = (reportId: string, tabKey?: string) => {
    // Skip contractor-ledger as it has its own component
    if (reportId === "contractor-ledger") {
      return null;
    }
    // Handle tab-specific APIs for reports with multiple tabs
    if (reportId === "wo-executed-pending-approved" && tabKey) {
      const tabApiMapping: Record<string, string> = {
        pending: "executedWosPending",
        approved: "executedWosApproved",
      };
      return tabApiMapping[tabKey];
    }

    // Default API mapping for single-view reports
    const apiMapping: Record<string, string> = {
      "wo-portfolio-summary": "WorkOrderPortfolioSummary",
      "wo-status-distribution": "workOrderStatusDistribution",
      "wo-executed-pending-approved": "executedWosPending",
      "payments-released-summary": "paymentsReleasedSummary",
      "budget-variance-report": "budgetVarianceReport",
      "master-status-counts": "masterStatusCounts",
      "internal-approval-queue": "internalApprovalQueue",
      "contractor-acceptance-tracker": "contractorAcceptanceTracker",
      "work-execution-pending-updates": "workExecutionPendingUpdates",
      "work-execution-approved-remaining": "workExecutionApprovedVsRemaining",
      "version-control-amendments": "versionControlAmendmentsRegister",
      "ra-bills-status-register": "raBillsStatusRegister",
      "ra-bill-aging": "raBillAging",
      "deductions-ledger": "deductionsLedger",
      "payments-register": "paymentsRegister",
      "outstanding-aging": "outstandingAging",
      "wo-vs-budget": "woVsBudget",
    };
    return apiMapping[reportId];
  };

  const fetchData = async (params: any, tabKey?: string) => {
    // Skip fetching data for contractor-ledger
    if (currentReportId === "contractor-ledger") {
      return;
    }

    setLoading(true);
    try {
      const {
        current,
        sortOrder,
        pageSize,
        direction,
        pageDiff,
        paginationId,
      } = params;

      const apiFunctionName = getApiFunction(
        currentReportId || "",
        tabKey || activeTab
      );

      if (!apiFunctionName || !(ReportsApis as any)[apiFunctionName]) {
        console.error(
          "API function not found for report:",
          currentReportId,
          "tab:",
          tabKey || activeTab
        );
        setDataSource([]);
        setPagination((prev) => ({
          ...prev,
          total: 0,
        }));
        setLoading(false);
        return;
      }

      const apiResponse = await (ReportsApis as any)[apiFunctionName]({
        sortOrder,
        pageSize,
        direction,
        pageDiff,
        paginationId,
        page: Number(current || params.page || pagination.current || 1),
        flowType: flowType || undefined,
      });

      const { data, status } = apiResponse;
      if (status) {
        const { data: list, totalCount: count } =
          data || apiResponse.response?.data || apiResponse.response || {};
        setDataSource(list || []);
        setPagination((prev) => ({
          ...prev,
          total: Number(count || 0),
        }));
      } else {
        setDataSource([]);
        setPagination((prev) => ({
          ...prev,
          total: 0,
        }));
      }
    } catch (error) {
      console.error("Error loading report data:", error);
      setDataSource([]);
      setPagination((prev) => ({
        ...prev,
        total: 0,
      }));
    } finally {
      setLoading(false);
    }
  };

  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams: any = {
      oldPage: pagination.current,
      current: newPagination.current?.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
    };

    if (sorter.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize != pagination.pageSize) {
      apiParams.pageSize = newPagination.pageSize;
      const _currentPage =
        Math.floor(
          ((Number(pagination.current) - 1) * Number(pagination.pageSize)) /
          Number(newPagination.pageSize)
        ) + 1;
      apiParams.current = _currentPage.toString();
      apiParams.pageDiff = _currentPage - 1;
    } else {
      if (newPagination.current != pagination.current) {
        apiParams.direction =
          newPagination.current > pagination.current ? "F" : "B";
        apiParams.paginationId =
          apiParams.direction == "F"
            ? dataSource[dataSource.length - 1]?.paginationId
            : dataSource[0]?.paginationId;

        apiParams.pageDiff =
          Math.abs(newPagination.current - pagination.current) - 1;
      }
    }

    setPagination({
      ...pagination,
      current: Number(apiParams.current),
      pageSize: apiParams.pageSize,
    });
    await fetchData(apiParams);
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  useEffect(() => {
    if (currentReportId && reportConfig) {
      // For reports with tabs, the activeTab useEffect will handle the initial fetch
      // For reports without tabs, fetch data here
      if (!reportConfig.tabs || reportConfig.tabs.length === 0) {
        fetchData({
          oldPage: 1,
          current: pagination.current.toString(),
          sortOrder: "DESC",
          pageSize: pagination.pageSize.toString(),
          direction: "P",
          pageDiff: 0,
          paginationId: null,
        });
      }
    }
  }, [currentReportId]);

  useEffect(() => {
    if (
      currentReportId &&
      reportConfig &&
      activeTab &&
      reportConfig.tabs &&
      reportConfig.tabs.length > 0
    ) {
      // Reset pagination and fetch data when tab changes
      setPagination({
        current: 1,
        pageSize: 10,
        defaultCurrent: 1,
        defaultPageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30", "40", "50"],
        total: 0,
      });

      fetchData(
        {
          oldPage: 1,
          current: "1",
          sortOrder: "DESC",
          pageSize: "10",
          direction: "P",
          pageDiff: 0,
          paginationId: null,
        },
        activeTab
      );
    }
  }, [activeTab]);

  // Redirect contractor-ledger to its own component
  useEffect(() => {
    if (currentReportId === "contractor-ledger") {
      const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
      navigate(
        `${process.env.PUBLIC_URL}/work-order-reports/contractor-ledger${flowTypeQuery}`
      );
    }
  }, [currentReportId, navigate, flowType]);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
      navigate(`/work-order-reports${flowTypeQuery}`);
    }
  };

  const getBreadcrumbItems = () => {
    const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
    const items: any = [
      {
        title: <HomeOutlined />,
        onClick: () => navigate("/dashboard"),
        href: "#",
      },
      {
        title: "Work Order Reports",
        onClick: () => navigate(`/work-order-reports${flowTypeQuery}`),
        href: "#",
      },
    ];

    if (reportInfo) {
      items.push({
        title: reportInfo.name,
      });
    }

    return items;
  };

  if (!currentReportId) {
    return (
      <>
        <Head title="Work Order Reports" />
        <Content>
          <Alert
            message="Report Not Found"
            description="The requested report could not be found. Please select a valid report from the reports list."
            type="warning"
            showIcon
            action={
              <Button
                size="small"
                onClick={() => {
                  const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
                  navigate(`/work-order-reports${flowTypeQuery}`);
                }}
              >
                Back to Reports
              </Button>
            }
          />
        </Content>
      </>
    );
  }

  if (!reportConfig || !reportInfo) {
    return (
      <>
        <Head title="Report Not Available" />
        <Content>
          <Alert
            message="Report Configuration Missing"
            description="This report is not yet implemented or configured. Please try another report."
            type="info"
            showIcon
            action={
              <Button
                size="small"
                onClick={() => {
                  const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
                  navigate(`/work-order-reports${flowTypeQuery}`);
                }}
              >
                Back to Reports
              </Button>
            }
          />
        </Content>
      </>
    );
  }

  const handleDrillDown = async (subReportId: string, record: any, columnKey?: string) => {
    const subReportConfig = getSubReportConfig(subReportId);

    if (!subReportConfig) {
      console.error(`Sub-report configuration not found for: ${subReportId}`);
      return;
    }

    setSubReportTitle(subReportConfig.title);
    setSubReportColumns(subReportConfig.columns);
    setSubReportLoading(true);
    setSubReportVisible(true);
    setActiveSubReportId(subReportId);
    setDrillDownRecord(record);

    try {
      const apiFunctionName = getSubReportApiFunction(subReportId);

      if (!apiFunctionName || !(ReportsApis as any)[apiFunctionName]) {
        console.error("API function not found for sub-report:", subReportId);
        setSubReportData([]);
        setSubReportLoading(false);
        return;
      }

      let params: any = {
        page: 1,
        pageSize: 100,
        flowType: flowType || undefined,
      };

      switch (subReportId) {
        case 'work-order-portfolio-drilldown':
          params.projectId = record.projectId || record.id;
          params.siteId = record.siteId;
          break;

        case 'payments-released-ra-bills-breakup':
          params.contractorId = record.contractorId;
          params.projectId = record.projectId;
          break;

        case 'contractor-acceptance-tracker-workOrder':
          params.workOrderIds = record.woNumber;
          params.contractorId = record.contractorId;
          break;

      }

      const response = await (ReportsApis as any)[apiFunctionName](params);

      if (response?.status) {
        const subReportData = response.data?.data || response.data || [];
        setSubReportData(subReportData);
      } else {
        console.error("Sub-report API failed:", response?.message);
        setSubReportData([]);
      }
    } catch (error) {
      console.error('Error loading sub-report:', error);
      setSubReportData([]);
    } finally {
      setSubReportLoading(false);
    }
  };

  const handleNestedDrillDown = async (subReportId: string, record: any) => {
    const subReportConfig = getSubReportConfig(subReportId);

    if (!subReportConfig) {
      console.error(`Nested sub-report configuration not found for: ${subReportId}`);
      return;
    }

    setNestedSubReportTitle(subReportConfig.title);
    setNestedSubReportColumns(subReportConfig.columns);
    setNestedSubReportLoading(true);
    setNestedSubReportVisible(true);
    setActiveNestedSubReportId(subReportId);
    setNestedDrillDownRecord(record);

    try {
      const apiFunctionName = getSubReportApiFunction(subReportId);

      if (!apiFunctionName || !(ReportsApis as any)[apiFunctionName]) {
        console.error("API function not found for nested sub-report:", subReportId);
        setNestedSubReportData([]);
        setNestedSubReportLoading(false);
        return;
      }

      let params: any = {
        page: 1,
        pageSize: 100,
        flowType: flowType || undefined,
      };

      switch (subReportId) {
        case 'contractor-acceptance-baseline-summary':
          params.workOrderLineIds = record.workOrderLineId;
          params.workOrderIds = record.workOrderId;
          params.contractorId = record.contractorId;
          break;

      }

      const response = await (ReportsApis as any)[apiFunctionName](params);

      if (response?.status) {
        const nestedSubReportData = response.data?.data || response.data || [];
        setNestedSubReportData(nestedSubReportData);
      } else {
        console.error("Nested sub-report API failed:", response?.message);
        setNestedSubReportData([]);
      }
    } catch (error) {
      console.error('Error loading nested sub-report:', error);
      setNestedSubReportData([]);
    } finally {
      setNestedSubReportLoading(false);
    }
  };

  const handleSubReportRowClick = (record: any, nestedSubReportId?: string) => {
    if (nestedSubReportId) {
      handleNestedDrillDown(nestedSubReportId, record);
    }
  };

  const renderTable = (columns: any[]) => {
    const enhancedColumns = columns.map((column) => {
      const col = { ...column };

      if (currentReportId === 'wo-portfolio-summary' && column.dataIndex === 'totalWorkOrdersValue') {
        col.render = (text: any, record: any, index: number) => (
          <span
            style={{
              color: '#1890ff',
              cursor: 'pointer',
            }}
            onClick={() => handleDrillDown('work-order-portfolio-drilldown', record)}
          >
            {text}
          </span>
        );
      }

      else if (currentReportId === 'payments-released-summary' && column.dataIndex === 'approvedRABillsValue') {
        col.render = (text: any, record: any, index: number) => (
          <span
            style={{
              color: '#1890ff',
              cursor: 'pointer',
            }}
            onClick={() => handleDrillDown('payments-released-ra-bills-breakup', record)}
          >
            {text}
          </span>
        );
      }

      else if (currentReportId === 'contractor-acceptance-tracker') {
        if (column.dataIndex === 'woNumber') {
          col.render = (text: any, record: any, index: number) => (
            <span
              style={{
                color: '#1890ff',
                cursor: 'pointer',

              }}
              onClick={() => handleDrillDown('contractor-acceptance-tracker-workOrder', record)}
            >
              {text}
            </span>
          );
        }
      }

      return col;
    });

    return (
      <FormProvider {...methods}>
        <Form component={false}>
          <Table
            className="customTable"
            columns={enhancedColumns}
            dataSource={dataSource}
            pagination={pagination}
            onChange={tableOnChange}
            bordered
            size="middle"
            scroll={{
              x: 1500,
            }}
            onRow={(record) => {
              return {
                onDoubleClick: () => {
                  // Handle row double-click if needed
                },
              };
            }}
          />
        </Form>
      </FormProvider>
    );
  };

  const getExportModuleKey = () => {
    if (currentReportId === "contractor-ledger") {
      return null;
    }
    if (currentReportId === "wo-executed-pending-approved") {
      return activeTab === "approved"
        ? "executed-wos-approved"
        : "wo-executed-pending-approved";
    }
    return currentReportId;
  };

  return (
    <>
      <Head title={reportInfo?.name || ""} />
      <Content>
        <Card className="card-bordered">
          <div className="card-inner card-inner-lg">
            <div style={{ marginBottom: 16 }}>
              <Breadcrumb items={getBreadcrumbItems()} />
            </div>

            <BlockHead size="lg">
              <BlockBetween>
                <BlockHeadContent>
                  <BlockTitle tag="h4">{reportInfo.name}</BlockTitle>
                  <p style={{ margin: 0, color: "#666" }}>
                    {reportInfo.description}
                  </p>
                </BlockHeadContent>
                <BlockHeadContent>
                  <Space>
                    <Export
                      data={{
                        sortOrder: "DESC",
                        pageSize: 100,
                        direction: "P",
                        pageDiff: 0,
                        flowType: flowType || undefined,
                      }}
                      moduleKey={getExportModuleKey()}
                      showAsButton={true}
                    />
                    <Button
                      icon={<ArrowLeftOutlined />}
                      onClick={handleBack}
                      type="primary"
                      ghost
                    >
                      Back
                    </Button>
                  </Space>
                </BlockHeadContent>
              </BlockBetween>
            </BlockHead>

            <div className="content clearfix">
              {loading ? (
                <Flex
                  align="center"
                  gap="middle"
                  className="pageLoader"
                  style={{ padding: "60px 0" }}
                >
                  <Spin size="large" className="m-auto" />
                </Flex>
              ) : dataSource?.length > 0 ? (
                <div
                  style={{
                    // width: screenSize - 100 + 'px',
                    margin: "0px auto",
                  }}
                >
                  {reportConfig.tabs && reportConfig.tabs.length > 0 ? (
                    <Tabs
                      activeKey={activeTab}
                      onChange={setActiveTab}
                      items={reportConfig.tabs.map((tab) => ({
                        key: tab.key,
                        label: tab.label,
                        children: renderTable(tab.columns),
                      }))}
                    />
                  ) : (
                    renderTable(reportConfig.columns || [])
                  )}
                </div>
              ) : (
                <div
                  style={{
                    height: 260,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Empty description="No data found" />
                </div>
              )}
            </div>

            {/* Report Information Footer */}
            {/* <Card
              size="small"
              style={{ marginTop: 16, backgroundColor: "#fafafa" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <strong>Report Info:</strong>
                  <span style={{ marginLeft: 8, color: "#666" }}>
                    Category: {reportInfo.category} | Created by:{" "}
                    {reportInfo.createdBy}
                  </span>
                </div>
                <div style={{ color: "#666" }}>
                  Last Updated: {reportInfo.lastUpdated}
                </div>
              </div>
            </Card> */}
          </div>
        </Card>
      </Content>

      <SubReportDrawer
        open={subReportVisible}
        onClose={() => {
          setSubReportVisible(false);
          setSubReportData([]);
          setSubReportColumns([]);
          setActiveSubReportId('');
        }}
        title={subReportTitle}
        columns={subReportColumns}
        data={subReportData}
        loading={subReportLoading}
        width={1400}
        onRowClick={handleSubReportRowClick}
        currentSubReportId={activeSubReportId}
      />

      <SubReportDrawer
        open={nestedSubReportVisible}
        onClose={() => {
          setNestedSubReportVisible(false);
          setNestedSubReportData([]);
          setNestedSubReportColumns([]);
          setActiveNestedSubReportId('');
        }}
        title={nestedSubReportTitle}
        columns={nestedSubReportColumns}
        data={nestedSubReportData}
        loading={nestedSubReportLoading}
        width={1200}
        currentSubReportId={activeNestedSubReportId}
      />
    </>
  );
};

export default ReportView;